'use client';

import { giftQuery } from '@/apis/gift/gift.api';
import { NewGiftCouponDto } from '@/apis/gift/gift.interface';
import JDILogin from '@/app/login/utils';
import UKDrawer from '@/components/UKDrawer';
import LottieAnimation from '@/components/lottieAnimation/lottieAnimation';
import { useTranslation } from '@/i18n/client';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/30462';
import { generateOneLink } from '@/utils/deepLink';
import { uaParse } from '@/intl-jdi-common-ssr/utils/uaParser';
import to from 'await-to-js';
import dayjs from 'dayjs';
import { useRouter } from 'next/navigation';
import { FC, memo, useEffect, useState } from 'react';
import giftLottie from '../../icon/giftLottie.json';
import './index.css';
import styled from './index.module.scss';
import { useStores } from '@/models';
// ---------------- 监控 ---------------- //
import {
  NEW_COMER_EVENT_NAMES,
  newComerBusinessLogger,
  NEW_COMER_TRACK_TYPE,
} from '@/utils/logger/instance/newComerBusinessLogger';

// 缓存接口定义
interface GiftModalCache {
  shown: boolean;
  date: string;
  userPin?: string; // 用户标识
  timestamp: number; // 时间戳
}

const NewUserDiscountsM: FC = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { userInfoStore } = useStores();
  const [open, setOpen] = useState<boolean>(false);
  const [isLogin, setIsLogin] = useState(false);
  const [couponList, setCouponList] = useState<NewGiftCouponDto[] | null>(null); // 普通券
  const [shippingCoupons, setShippingCoupons] = useState<NewGiftCouponDto[] | null>(null); // 运费券
  const [activityTitle, setActivityTitle] = useState(''); //弹窗 title
  const [currencySymbol, setCurrencySymbol] = useState(''); //货币符号
  const [totalDiscount, setTotalDiscount] = useState(0); //优惠总金额
  const [richText, setRichText] = useState('');
  const [bottomText, setBottomText] = useState('');
  const [currentUserPin, setCurrentUserPin] = useState<string>('');

  const toggleDrawer = () => {
    setOpen(!open);
    handleClose();
  };

  useEffect(() => {
    const { isInJoybuyApp } = uaParse();
    // 如果isInJoybuyApp为true代表营销落地页，h5不需要弹窗，由APP处理
    if (isInJoybuyApp) {
      return;
    }
    loginStatus();
  }, []);

  const loginStatus = async () => {
    const { isLoggedIn, userInfo } = await JDILogin.getInstance().getUserInfo();
    setIsLogin(isLoggedIn);
    setCurrentUserPin(userInfo?.pin || '');
    getGift(userInfo);
  };

  const getGift = async (user: any) => {
    const [err, result] = await to(giftQuery({ scene: 'QUERY_RECEIVE' }));
    console.log('cjy--------', err, result);
    if (err || result?.code !== '200') {
      newComerBusinessLogger({
        eventName: NEW_COMER_EVENT_NAMES.NEW_COMER_POPUP_INIT_ERROR,
        category: {
          type: NEW_COMER_TRACK_TYPE.NEW_COMER_API_ERROR,
        },
        message: {
          error: err?.message || '-100',
          code: result?.code || '-100',
          traceId: result?.traceId || '-100',
        },
      });
    }
    if (err) {
      setCouponList(null);
      setShippingCoupons(null);
    } else {
      if (result?.data?.activityTitle) {
        setActivityTitle(result?.data?.activityTitle);
      }
      if (result?.data?.noLoginContent) {
        setBottomText(result?.data?.noLoginContent);
      }
      setCurrencySymbol(result?.data?.currencySymbol);
      setTotalDiscount(result?.data?.couponAmount);
      const list = result?.data?.couponList;
      const text = result?.data?.activityContent;
      const isLogonApp = result?.data?.isLogonApp && result?.data?.isLogonApp === 1 ? true : false;
      const { isInOchamaApp } = uaParse();
      // 新弹窗逻辑：仅当有优惠券且shouldShowModal返回true时且不在ochama app里时弹窗
      if (list && list.length > 0 && shouldShowModal(user, list, isLogonApp) && !isInOchamaApp) {
        setOpen(true);
        setRichText(text);
        setCouponList(result?.data?.productCoupons);
        setShippingCoupons(result?.data?.shippingCoupons);
        saveModalCache(user?.pin);
      }
    }
  };

  /**
   * 判断是否应该显示弹窗
   * 1. 本地缓存里没有giftModalShownH5字段时，弹出弹窗
   * 2. 登录之后退出登录切换新账号，且couponList不为空时，弹出弹窗
   * 3. 缓存清除之后弹出弹窗
   * 4. 缓存保留30天
   * @param user 当前用户信息
   * @param couponList 当前优惠券列表
   */
  const shouldShowModal = (user: any, couponList: any[], isLogonApp: boolean): boolean => {
    const cacheKey = 'giftModalShownH5';
    const cachedData = localStorage.getItem(cacheKey);

    // 在别端首登后不弹窗
    if (isLogonApp) {
      return false;
    }

    // couponList判空，如无券不弹窗
    if (!couponList || couponList.length === 0) {
      return false;
    }

    // 情况1: 本地缓存里没有giftModalShownH5字段时，弹出弹窗
    if (!cachedData) {
      return true;
    }

    try {
      const cache: GiftModalCache = JSON.parse(cachedData);
      const now = Date.now();
      const thirtyDaysInMs = 24 * 60 * 60 * 1000; // 30天的毫秒数

      // 情况3: 缓存超过30天，清除缓存并弹出弹窗
      if (now - cache.timestamp > thirtyDaysInMs) {
        localStorage.removeItem(cacheKey);
        return true;
      }

      // 情况2: 登录用户切换账号 或退出登录再登录新账号
      if (user?.pin) {
        // 如果缓存中没有用户信息，或者用户PIN不同，说明是新用户或切换了账号
        if (!cache.userPin || cache.userPin !== user.pin) {
          return true;
        }
        // 同一用户，检查缓存是否过期
        return false;
      } else {
        // 未登录用户，检查缓存中是否有用户信息
        // 如果缓存中有用户信息，说明之前是登录状态，现在退出了，需要重新显示
        if (cache.userPin) {
          return true;
        }
        // 未登录且缓存中也没有用户信息，不显示
        return false;
      }
    } catch (error) {
      // 缓存数据格式错误，清除缓存并显示弹窗
      console.error('解析缓存数据失败:', error);
      localStorage.removeItem(cacheKey);
      return true;
    }
  };

  /**
   * 保存弹窗显示记录到缓存
   * @param userPin 用户PIN
   */
  const saveModalCache = (userPin?: string) => {
    const cacheKey = 'giftModalShownH5';
    const now = Date.now();
    const cache: GiftModalCache = {
      shown: true,
      date: dayjs().format('YYYY-MM-DD'),
      userPin: userPin || undefined,
      timestamp: now,
    };

    localStorage.setItem(cacheKey, JSON.stringify(cache));
  };

  // 保持原有的saveDate方法，用于兼容
  const saveDate = () => {
    saveModalCache(currentUserPin);
  };

  const handleClose = () => {
    // @ts-ignore
    sendClickTrack.NewCustomerGift_GiftLayer_Close({
      page_id: 'home',
      ...track(),
    });
    userInfoStore.fetchUserInfo();
    saveDate();
    setOpen(false);
  };

  const handleLogin = () => {
    handleClose();
    // 没下载app，去下载页面
    const url = generateOneLink();
    console.log(url);
    url && (window.location.href = url);
    // TODO List: 已下载app，唤起app

    // 以前的逻辑，跳转登录
    // const returnUrl = window.location.pathname + window.location.search;
    // JDILogin.getInstance().goToLoginPage({
    //   returnUrl: returnUrl,
    // });
  };

  const track = () => {
    const coupons: any[] = [];
    const list = [...(couponList || []), ...(shippingCoupons || [])];
    if (list && list.length > 0) {
      list.forEach((item: any) => {
        coupons.push({
          couponId: item?.couponId,
          batchId: item?.batchId || '-100',
          couponTag: '0', // 0 新人礼包
          use_threshold: item?.quota,
          couponType: item?.couponType === 1 ? '1' : '2',
          couponSave: item?.discount,
        });
      });
    }
    return {
      coupon: coupons,
      is_login: isLogin ? ('1' as const) : ('0' as const),
    };
  };

  // ---------------- 风格化文字 ---------------- //
  const formatRed = (redText: any) => {
    return `<span style="font-weight: 700; color: #CC0C1C;">${redText}</span>`;
  };
  const formatBold = (boldText: any) => {
    return `<span style="font-weight: 700; color: #1A1A1A;">${boldText}</span>`;
  };

  // ---------------- 单独处理优惠券/运费券多语言文案 ---------------- //
  const couponInfo = (list: any) => {
    let text = '';
    if (list && list.length > 0) {
      text = t('gift.couponDetail', { num: list?.length });
      list.forEach((item: any, index: number) => {
        if (index > 0) {
          text += t('gift.connector'); // 在不是第一个元素时添加 +
        }
        text += t('gift.offText', {
          discount: currencySymbol + item?.discount,
          quota: currencySymbol + item?.quota,
        });
      });
    }
    return text;
  };

  const deliveryInfo = (list: any) => {
    if (list && list.length > 0) {
      return t('gift.deliveryInfo', { num: list.length });
    }
  };

  // ---------------- 概括优惠券信息文案处理 ---------------- //
  const couponPromptText = () => {
    let text = '';
    if (shippingCoupons && shippingCoupons.length > 0 && couponList && couponList.length > 0) {
      text = t('gift.totalDiscount')
        .replace('##discount##', `${formatRed(`${currencySymbol}${totalDiscount}`)}`)
        .replace('##num##', `${formatRed(shippingCoupons.length)}`);
    } else if (shippingCoupons && shippingCoupons.length > 0) {
      text = `${listShippingText()}`;
    } else if (couponList && couponList.length > 0) {
      text = `${listCouponText()}`;
    }
    const freeDeliveryText = t('gift.freeBold'); // 获取动态文本
    // 这里自定义（##discount##、free-delivery）根据多语言匹配规则
    return text.replace(/free-delivery/g, formatBold(freeDeliveryText));
  };

  const listCouponText = () => {
    if (couponList && couponList.length > 0) {
      return t('gift.freeNum')
        .replace('##discount##', `${formatRed(`${currencySymbol}${totalDiscount}`)}`)
        .replace('##num##', `${formatRed(couponList.length)}`);
    }
  };
  const listShippingText = () => {
    if (shippingCoupons && shippingCoupons.length > 0) {
      const text = t('gift.shippingNum').replace('##num##', `${formatRed(shippingCoupons.length)}`);
      const freeDeliveryText = t('gift.freeBold');
      return text.replace(/free-delivery/g, formatBold(freeDeliveryText));
    }
  };

  return (
    <>
      <UKDrawer
        openDrawer={open}
        includeBottomBar={false}
        setOpenDrawer={toggleDrawer}
        className='newUserDis'
        hideTitleButton={false}
        height='auto'
      >
        <div
          className={styled.newUserDisMain}
          exptag={'exp|' + EXPOSURE_RECORD.NewCustomerGift_GiftLayer_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0.01, "repeated":false, "area_rate": 0.01}'
          data-exptag-json-param={JSON.stringify(track())}
        >
          <p className={styled.title}>{activityTitle}</p>
          <LottieAnimation animationData={giftLottie} width={204} height={156} loop={false} />
          <div className={styled.newUserDisWhiteArea}>
            <p dangerouslySetInnerHTML={{ __html: richText }} />
            {/* <p className={styled.overview} dangerouslySetInnerHTML={{ __html: couponPromptText() }} />
          <p className={styled.detail}>{couponList && couponList.length > 0 && couponInfo(couponList)}</p>
          <p className={styled.detail}>{shippingCoupons && shippingCoupons.length > 0 && deliveryInfo(shippingCoupons)}</p> */}
          </div>
          <div className={styled.btnBg}>
            <button
              className={styled.footerBtn}
              onClick={() => {
                // isLogin ? handleClose() : handleLogin();
                handleLogin();
                sendClickTrack.NewCustomerGift_GiftLayer({
                  text: richText,
                  ...track(),
                });
              }}
            >
              {t('gift.download')}
            </button>
          </div>
        </div>
      </UKDrawer>

      {/* {!isLogin && bottomText && <BottomBar signInText={bottomText} />} */}
    </>
  );
};

export default memo(NewUserDiscountsM);
